import { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import Header from "./components/Header";
import Hero from "./components/Hero";
import CTACards from "./components/CTACards";
import Partners from "./components/Partners";
import Services from "./components/Services";
import Testimonials from "./components/Testimonials";
import GoogleReviews from "./components/GoogleReviews";
import CTASection from "./components/CTASection";
import ContactForm from "./components/ContactForm";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import PasswordGenerator from "./components/PasswordGenerator";
import AIAutomation from "./components/AIAutomation";
import CookieConsent from "./components/CookieConsent";
import { Toaster } from "./components/ui/toaster";

// Lazy-loaded secondary pages — reduces initial bundle size
const CyberSecurity = lazy(() => import("./components/CyberSecurity"));
const Newsletter = lazy(() => import("./components/Newsletter"));
const NewsletterAdmin = lazy(() => import("./components/NewsletterAdmin"));
const NewsletterConfirm = lazy(() => import("./components/NewsletterConfirm"));
const NewsletterUnsubscribe = lazy(() => import("./components/NewsletterUnsubscribe"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const RemoteSupport = lazy(() => import("./components/RemoteSupport"));
const OpenTicket = lazy(() => import("./components/OpenTicket"));
const AboutUs = lazy(() => import("./components/AboutUs"));
const CaseStudies = lazy(() => import("./components/CaseStudies"));
const WebsitesApps = lazy(() => import("./components/WebsitesApps"));
const Microsoft365Page = lazy(() => import("./components/Microsoft365Page"));
const ITSupportPage = lazy(() => import("./components/ITSupportPage"));
const ServerMonitoringPage = lazy(() => import("./components/ServerMonitoringPage"));
const HardwareSupplyPage = lazy(() => import("./components/HardwareSupplyPage"));
const VoIPTelecomsPage = lazy(() => import("./components/VoIPTelecomsPage"));
const BlogPage = lazy(() => import("./components/BlogPage"));
const CSRPolicy = lazy(() => import("./components/policies/CSRPolicy"));
const DataProtectionPolicy = lazy(() => import("./components/policies/DataProtectionPolicy"));
const AntiSlaveryPolicy = lazy(() => import("./components/policies/AntiSlaveryPolicy"));
const AntiBriberyPolicy = lazy(() => import("./components/policies/AntiBriberyPolicy"));
const CookiesPolicy = lazy(() => import("./components/policies/CookiesPolicy"));
const PrivacyPolicy = lazy(() => import("./components/policies/PrivacyPolicy"));

const API = `/api`;

const Home = () => {
  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
    
    // Handle hash-based navigation (e.g., /#services)
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.replace('#', '');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <SEO
        title="Managed IT & Telecoms | Glossop"
        description="Trusted IT support, cyber security, VoIP and Microsoft 365 for businesses in Glossop and Northwest England since 1998."
        path="/"
      />
      <Hero />
      <CTACards />
      <section id="support">
        <Partners />
      </section>
      <Services />
      <PasswordGenerator />
      <AIAutomation />
      <Testimonials />
      <GoogleReviews />
      <CTASection />
      <ContactForm />
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <Header />
        <Suspense fallback={<div className="min-h-screen" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/cybersecurity" element={<><SEO title="Cyber Security" description="Protect your business with Huntress managed cyber security solutions. Managed EDR, ITDR, SAT and SIEM delivered by Binary Consultancy in Glossop, serving SMEs across the Northwest of England." path="/cybersecurity" keywords="cybersecurity Glossop, managed EDR, Huntress security, endpoint protection SME, identity threat detection, SIEM Northwest England" /><CyberSecurity /></>} />
          <Route path="/about" element={<><SEO title="About Us" description="About Binary Consultancy Limited - UK-based managed IT support, cybersecurity, cloud services and business telecoms provider. Serving SMEs across the High Peak and Northwest England since 1998. Led by Richard Dyer and Nathan Dearn." path="/about" keywords="about Binary Consultancy, IT company Glossop, managed IT provider High Peak, SME IT support Northwest, Richard Dyer Nathan Dearn, UK IT telecoms company" /><AboutUs /></>} />
          <Route path="/newsletter" element={<><SEO title="Newsletter" description="Subscribe to the Binary Consultancy newsletter for the latest IT and telecoms news, cybersecurity tips and technology updates for SMEs in the Northwest of England." path="/newsletter" keywords="IT newsletter, cybersecurity updates SME, Binary Consultancy news, technology tips small business" /><Newsletter /></>} />
          <Route path="/newsletter/admin" element={<NewsletterAdmin />} />
          <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />
          <Route path="/newsletter/unsubscribe/:subscriberId" element={<NewsletterUnsubscribe />} />
          <Route path="/remote-support" element={<><SEO title="IT Maintenance Services" description="Professional IT maintenance and managed support services from Binary Consultancy. Proactive monitoring, helpdesk support, server management. Based in Glossop, serving SMEs across the Northwest of England." path="/remote-support" keywords="IT maintenance Glossop, managed IT support, remote IT support Northwest, proactive server monitoring, helpdesk support SME" /><RemoteSupport /></>} />
          <Route path="/support" element={<Navigate to="/remote-support" replace />} />
          <Route path="/open-ticket" element={<><SEO title="Open a Support Ticket" description="Need IT help? Open a support ticket with Binary Consultancy. Fast response times for businesses in Glossop, Manchester, Stockport and across the Northwest." path="/open-ticket" keywords="IT support ticket, Binary Consultancy helpdesk, IT help Glossop Manchester, fast IT response" /><OpenTicket /></>} />
          <Route path="/case-studies" element={<><SEO title="Case Studies" description="See how Binary Consultancy has helped SMEs across the Northwest improve their IT, strengthen security and reduce downtime. Real results for real businesses." path="/case-studies" keywords="IT case studies, managed IT results, cybersecurity case study SME, Binary Consultancy clients" /><CaseStudies /></>} />
          <Route path="/websites-apps" element={<><SEO title="Websites & Apps" description="Custom website design, web application development and AI-powered tools from Binary Consultancy. From simple brochure sites to complex web apps — built for SMEs across the Northwest of England." path="/websites-apps" keywords="website design Glossop, web development Manchester, custom web apps, AI applications, React developer Northwest, WordPress developer, e-commerce website SME" /><WebsitesApps /></>} />
          <Route path="/microsoft-365" element={<Microsoft365Page />} />
          <Route path="/it-support" element={<ITSupportPage />} />
          <Route path="/server-monitoring" element={<ServerMonitoringPage />} />
          <Route path="/hardware-supply" element={<HardwareSupplyPage />} />
          <Route path="/voip-telecoms" element={<VoIPTelecomsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/policies/csr" element={<CSRPolicy />} />
          <Route path="/policies/data-protection" element={<DataProtectionPolicy />} />
          <Route path="/policies/anti-slavery" element={<AntiSlaveryPolicy />} />
          <Route path="/policies/anti-bribery" element={<AntiBriberyPolicy />} />
          <Route path="/policies/cookies" element={<CookiesPolicy />} />
          <Route path="/policies/privacy" element={<PrivacyPolicy />} />
        </Routes>
        </Suspense>
        <Footer />
        <CookieConsent />
        <Toaster />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;